import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LIKERT = [
    { v: 1, l: "Strongly disagree" }, { v: 2, l: "Disagree" }, { v: 3, l: "Neutral" },
    { v: 4, l: "Agree" }, { v: 5, l: "Strongly agree" },
];

export default function LandingQuiz() {
    const [survey, setSurvey] = useState(null);
    const [answers, setAnswers] = useState({});
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const setA = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

    useEffect(() => { api.get("/public/surveys/active").then((r) => setSurvey(r.data)).catch(() => {}); }, []);

    const submit = async () => {
        const missing = survey.questions.filter((q) => q.type !== "text" && answers[q.id] == null);
        if (missing.length) { toast.error("Please answer all rating questions"); return; }
        setSubmitting(true);
        try {
            await api.post(`/public/surveys/${survey.id}/responses`, { answers, ethnicity: null, band: null });
            setDone(true);
            toast.success("Thank you — your anonymous response was recorded");
        } catch { toast.error("Could not submit response"); }
        finally { setSubmitting(false); }
    };

    if (!survey) return null;

    return (
        <section id="pulse" className="bg-nhs-ink text-white scroll-mt-20" data-testid="landing-quiz">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="inline-flex items-center gap-2 rounded-full bg-nhs-teal/15 text-nhs-teal px-4 py-1.5 text-sm font-semibold">
                        <Lock className="h-4 w-4" /> 100% anonymous · no sign-in needed
                    </span>
                    <h2 className="font-heading text-3xl font-bold mt-5">{survey.title}</h2>
                    <p className="mt-3 text-slate-300">{survey.description}</p>
                </div>

                {done ? (
                    <div className="mt-10 bg-white rounded-2xl p-10 text-center text-nhs-ink" data-testid="landing-quiz-done">
                        <div className="h-14 w-14 rounded-full bg-nhs-teal/10 flex items-center justify-center mx-auto"><CheckCircle2 className="h-7 w-7 text-nhs-teal" /></div>
                        <h3 className="font-heading text-xl font-bold mt-4">Thank you for shaping the culture</h3>
                        <p className="text-slate-500 mt-2">Your anonymous feedback goes straight into the equity insight your Trust acts on.</p>
                    </div>
                ) : (
                    <div className="mt-10 bg-white rounded-2xl p-6 sm:p-8 text-nhs-ink space-y-6">
                        {survey.questions.map((q, idx) => (
                            <div key={q.id} data-testid={`landing-question-${q.id}`}>
                                <p className="font-semibold text-sm sm:text-base"><span className="text-nhs-blue mr-2">{idx + 1}.</span>{q.prompt}</p>
                                {q.type === "likert" && (
                                    <div className="mt-3 grid grid-cols-5 gap-2">
                                        {LIKERT.map((o) => {
                                            const active = answers[q.id] === o.v;
                                            return (
                                                <button key={o.v} type="button" onClick={() => setA(q.id, o.v)} data-testid={`landing-likert-${q.id}-${o.v}`}
                                                    className={`rounded-md border p-2 text-[11px] sm:text-xs font-medium transition-colors duration-200 ${active ? "bg-nhs-blue text-white border-nhs-blue" : "bg-white border-slate-300 text-slate-600 hover:border-nhs-blue hover:text-nhs-blue"}`}>
                                                    <span className="block text-base font-bold">{o.v}</span>{o.l}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {q.type === "single_choice" && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {q.options.map((o) => {
                                            const active = answers[q.id] === o;
                                            return (
                                                <button key={o} type="button" onClick={() => setA(q.id, o)} data-testid={`landing-choice-${q.id}-${o}`}
                                                    className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${active ? "bg-nhs-blue text-white border-nhs-blue" : "bg-white border-slate-300 text-slate-600 hover:border-nhs-blue"}`}>
                                                    {o}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {q.type === "text" && (
                                    <Textarea rows={3} className="mt-3" value={answers[q.id] || ""} onChange={(e) => setA(q.id, e.target.value)} placeholder="Share your thoughts (optional)…" data-testid={`landing-text-${q.id}`} />
                                )}
                            </div>
                        ))}
                        <Button onClick={submit} disabled={submitting} className="bg-nhs-blue hover:bg-nhs-darkblue text-white w-full sm:w-auto px-8" data-testid="landing-submit-quiz">
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit anonymously"}
                        </Button>
                    </div>
                )}
            </div>
        </section>
    );
}
